/*
 * THESIS: Use the home landing page’s visual code for the comparison page.
 * OWN-WORLD: Shared forge hero, title spans, badge, CTA and section typography.
 * STORY: Recognize the alternative, find the trial, compare existing facts.
 * FIRST VIEWPORT: The home page’s centered hero composition with unchanged copy.
 * FORM: Existing sections, shared landing classes; custom CSS only for the table.
 */
import { createFileRoute, Link } from "@tanstack/react-router"
import { ComparisonValue } from "@/components/comparison-value"
import { LandingSourceForm } from "@/components/landing-source-form"
import { Brand, PublicNavbar } from "@/components/public-navbar"
import { PublicFaq } from "@/components/public-faq"
import { SavingHoursSection } from "@/components/saving-hours-section"
import { Badge } from "@/components/ui/badge"
import { buttonVariants } from "@/components/ui/button"
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { wayinComparison } from "@/lib/wayin-comparison"
import { publicPageHead } from "@/lib/seo"

export const Route = createFileRoute("/wayin-ai-alternative")({
  head: () =>
    publicPageHead({
      path: "/wayin-ai-alternative",
      title: "Wayin AI Alternative for YouTube & Twitch | intoshort",
      description:
        "Looking for a Wayin AI alternative? Try intoshort with one local video and up to 3 watermark-free 1080p shorts. Edit the cuts, captions and framing.",
    }),
  component: WayinAlternativePage,
})

function WayinAlternativePage() {
  return (
    <div className="landing landing-home forge-landing comparison-page">
      <a className="skip-link" href="#contenu">
        Skip to content
      </a>
      <PublicNavbar />
      <main id="contenu" className="forge-main">
        <section className="forge-hero page-width">
          <div className="forge-hero-copy">
            <Badge variant="outline" className="forge-badge">
              For YouTube creators and Twitch streamers
            </Badge>
            <h1>
              <span className="forge-title-main">A Wayin AI alternative.</span>{" "}
              <span className="forge-title-sub">
                Your clips,{" "}
                <span className="forge-heading-accent">your call.</span>
              </span>
            </h1>
            <p className="forge-hero-lead">
              Intoshort is an AI tool like Wayin AI that turns long-form videos
              into short clips.
            </p>
            <LandingSourceForm />
          </div>
        </section>

        <section className="comparison-trial" aria-labelledby="trial-heading">
          <div className="forge-final-cta page-width">
            <div>
              <h2 id="trial-heading">Find your best clips with Intoshort.</h2>
              <span>
                Start with one source video and up to three accessible shorts.
                Edit the cuts, captions and framing before downloading.
              </span>
              <span className="comparison-trial-terms">
                One-time free trial. Results depend on the footage. Additional
                shorts require a paid plan; upload and processing limits apply.
              </span>
              <LandingSourceForm showTrustLine={false} />
            </div>
          </div>
        </section>

        <section
          id="feature-comparison"
          className="forge-how comparison-features page-width"
          aria-labelledby="comparison-heading"
        >
          <header className="forge-section-intro comparison-intro">
            <h2 id="comparison-heading" className="mx-auto text-center">
              What Intoshort offers
              <br />
              compared with Wayin AI.
            </h2>
            <Link to="/app" className={buttonVariants({ size: "lg" })}>
              Try Intoshort
            </Link>
          </header>
          <div
            className="comparison-table-region comparison-table-region-no-bottom-border"
            role="region"
            aria-label="Selected IntoShort benefits compared with Wayin AI"
            tabIndex={0}
          >
            <Table className="comparison-table">
              <TableCaption className="text-center">
                Selected Intoshort benefits. Last updated:{" "}
                <time dateTime="2026-11-03">November 3, 2026</time>.
              </TableCaption>
              <TableHeader>
                <TableRow>
                  <TableHead scope="col">
                    <span className="sr-only">Intoshort benefits</span>
                  </TableHead>
                  <TableHead scope="col">
                    <Brand />
                  </TableHead>
                  <TableHead scope="col">Wayin AI</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {wayinComparison.map((row) => (
                  <TableRow key={row.feature}>
                    <TableHead scope="row">{row.feature}</TableHead>
                    <TableCell className="comparison-intoshort-cell">
                      <ComparisonValue value={row.intoshort} />
                    </TableCell>
                    <TableCell>
                      <ComparisonValue value={row.wayin} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </section>
        <SavingHoursSection />
        <PublicFaq />
      </main>
    </div>
  )
}
